// Lens-agnostic vocabulary + derivations for the kanta/salkku overview grammar every perspective
// that has an overview shares (docs/spec/concepts.md §2.4 → Portfolio-scope overview
// grammar). Konfiguraatio fills it with setup questions (`config-setup-lens.ts`); the three
// judgment lenses fill it with finding buckets (`lens-findings-overview.ts`, story #1296 task
// #1308). Both reduce into the types below so `PerspectiveOverview` and its three child
// components (`OverviewSituationCards`, `OverviewBuildingList`, `OverviewMap`) render every lens
// through one composition.
//
// One derivation is already lens-independent and lives here rather than being copied per lens: a
// generic bucket builder, generalizing Konfiguraatio's former `deriveSetupBuckets` over a
// caller-supplied key order + label lookup. No React, no fetch, no store dependency — same
// discipline as the module this vocabulary was lifted out of.
import type { LucideIcon } from "lucide-react";
import type { Building } from "@/lib/api/building";
import type { CardTone } from "@/lib/card-styles";

export interface OverviewBuildingRef {
  id: string;
  name: string;
  city: string;
}

export interface OverviewBucket {
  key: string;
  label: string;
  /** Per-lens icon (Konfiguraatio's question icons, a severity icon for a judgment lens, …) —
   *  carried on the bucket itself so `OverviewSituationCards` never has to know which lens it is
   *  rendering. */
  icon: LucideIcon;
  /** Tone for this bucket's situation card and the tile chips naming it — uniform "warn" for
   *  Konfiguraatio's setup questions, severity-graded for the judgment lenses. */
  tone: CardTone;
  count: number;
  /** Buildings in scope still holding this bucket's condition — feeds the narrowed tile grid when
   *  the situation card is selected as a facet. */
  buildings: OverviewBuildingRef[];
}

export interface PerBuildingOverview {
  buildingId: string;
  /** This lens's open questions/buckets for this building. Empty + `dataAvailable` = complete
   *  (Valmis / Kunnossa). */
  openQuestions: string[];
  complete: boolean;
  /** False when every underlying fetch failed for this building — there is nothing to judge by,
   *  which is not the same as "nothing to fix". Callers must render a distinct unknown state
   *  rather than complete when this is false (never fabricate completeness). */
  dataAvailable: boolean;
}

export interface OverviewLensState {
  /** True until every scoped building has settled (successfully or via isolated failure). */
  loading: boolean;
  settledCount: number;
  totalCount: number;
  /** The kanta/salkku + city/type scoped building set — the same input every block on the
   *  overview reads (situation cards, tile grid, map). */
  scopedBuildings: Building[];
  buckets: OverviewBucket[];
  perBuilding: Map<string, PerBuildingOverview>;
}

/**
 * One bucket per `keyOrder` entry — each lists the buildings in `buildings` (the already
 * kanta/salkku/city/type-scoped set) whose `PerBuildingOverview.openQuestions` includes that key.
 * A building whose data hasn't settled yet (no `perBuilding` entry) simply isn't counted yet —
 * buckets grow as the caller's progressive fan-out (or single scoped fetch) delivers more
 * buildings, never blocking on all of them.
 */
export function deriveOverviewBuckets<K extends string>(
  buildings: Building[],
  perBuilding: Map<string, PerBuildingOverview>,
  keyOrder: readonly K[],
  metaFor: (key: K) => { label: string; icon: LucideIcon; tone: CardTone },
): OverviewBucket[] {
  return keyOrder.map((key) => {
    const openBuildings = buildings.filter((b) =>
      perBuilding.get(b.id)?.openQuestions.includes(key),
    );
    return {
      key,
      ...metaFor(key),
      count: openBuildings.length,
      buildings: openBuildings.map((b) => ({
        id: b.id,
        name: b.name,
        city: b.city,
      })),
    };
  });
}

/**
 * Narrows `buildings` to those holding `facetKey`'s bucket open, or returns `buildings`
 * unchanged when no facet is selected (`facetKey === null`). Feeds both the tile grid and the
 * map when a situation card is selected as a facet — page-local, never written to the URL or
 * inventory scope.
 */
export function filterBuildingsByFacet(
  buildings: Building[],
  perBuilding: Map<string, PerBuildingOverview>,
  facetKey: string | null,
): Building[] {
  if (facetKey === null) return buildings;
  return buildings.filter((b) =>
    perBuilding.get(b.id)?.openQuestions.includes(facetKey),
  );
}

/** Per-lens copy for the two blocks whose text is lens-specific — everything else (chrome,
 *  tones, sort order, layout) is identical across lenses. */
export interface OverviewCopy {
  /** Situation card sub-line for a non-zero count, e.g. "3 kiinteistöä tarvitsee vielä". */
  situationCardSubtitle: (count: number) => string;
  /** Situation card sub-line when the bucket's count is zero. */
  situationCardAllDoneLabel: string;
  /** Situation card sub-line when the scoped building set itself is empty. */
  emptyScopeHint: string;
  /** Building-tile chip label when a building has no open buckets on this lens
   *  ("Valmis" for Konfiguraatio, "Kunnossa" for the judgment lenses). */
  tileCompleteLabel: string;
  mapTitle: string;
  mapSubtitle: string;
  mapLegendOkLabel: string;
  mapLegendWarnLabel: string;
}
